import { useCallback, useEffect, useRef } from 'react'
import type { EntityLinkDocument } from '@/lib/db/rxdb/hooks/entities/entity-links'
import {
  calculateTiptapContentLength,
  extractPlainTextFromTiptap,
} from '@/routes/features/tasks/utils/editorUtils'
import { reconcileInlineEntityLinks } from '@/components/features/backlinks/reconcileInlineEntityLinks'
import { LINK_TYPES } from '@/lib/db/rxdb/schemas/entityLink'

const SAVE_DEBOUNCE_MS = 600

interface ContentRecord {
  id: string
  content: string
  format?: string
  version?: number
}

interface UseEditorAutoSaveOptions {
  entityId: string | undefined
  entityType: string
  entityName: string
  envId: string | undefined
  userId: string | null
  content: ContentRecord | null | undefined
  existingLinks: Array<EntityLinkDocument>
  canEdit: boolean
  collaborative?: boolean
  createContent: (data: {
    content: string
    format: string
    length: number
    plainText: string
    version: number
  }) => Promise<void>
  updateContent: (
    id: string,
    data: {
      content: string
      format: string
      length: number
      plainText: string
      version: number
    },
  ) => Promise<void>
  createEntityLink: (...args: Array<any>) => Promise<any>
  deleteEntityLink: (...args: Array<any>) => Promise<any>
}

export function useEditorAutoSave({
  entityId,
  entityType,
  entityName,
  envId,
  userId,
  content,
  existingLinks,
  canEdit,
  collaborative = false,
  createContent,
  updateContent,
  createEntityLink,
  deleteEntityLink,
}: UseEditorAutoSaveOptions) {
  const lastSavedContentRef = useRef('')
  const pendingContentRef = useRef<string | null>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const displayContent = content?.content || ''

  useEffect(() => {
    if (collaborative) return
    if (pendingContentRef.current !== null) return
    lastSavedContentRef.current = displayContent
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
  }, [entityId, displayContent, collaborative])

  const flushPendingSave = useCallback(async () => {
    if (collaborative) return
    if (!pendingContentRef.current) {
      return
    }

    const contentToSave = pendingContentRef.current
    pendingContentRef.current = null
    saveTimeoutRef.current = null

    if (contentToSave === lastSavedContentRef.current) {
      return
    }

    try {
      const plainText = extractPlainTextFromTiptap(contentToSave)
      const length = calculateTiptapContentLength(contentToSave)

      if (!content) {
        await createContent({
          content: contentToSave,
          format: 'tiptap',
          length,
          plainText,
          version: 1,
        })
      } else {
        await updateContent(content.id, {
          content: contentToSave,
          format: 'tiptap',
          length,
          plainText,
          version: (content.version || 0) + 1,
        })
      }

      lastSavedContentRef.current = contentToSave

      if (entityId && userId && envId) {
        const inlineLinks = existingLinks.filter(
          (l) => l.linkType === LINK_TYPES.INLINE,
        )
        reconcileInlineEntityLinks({
          sourceEntityType: entityType,
          sourceEntityId: entityId,
          sourceEntityName: entityName,
          envId,
          userId,
          content: contentToSave,
          existingInlineLinks: inlineLinks,
          createEntityLink,
          deleteEntityLink,
        }).catch(() => {
          // Non-critical: entity link reconciliation failure shouldn't block saves
        })
      }
    } catch {
      // Silently handle save errors - content will be retried
    }
  }, [
    entityId,
    entityType,
    entityName,
    envId,
    userId,
    content,
    existingLinks,
    collaborative,
    createContent,
    updateContent,
    createEntityLink,
    deleteEntityLink,
  ])

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      void flushPendingSave()
    }
  }, [flushPendingSave])

  const handleContentChange = useCallback(
    (newContent: string) => {
      if (!canEdit || collaborative) return
      if (newContent === lastSavedContentRef.current) return

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      pendingContentRef.current = newContent
      saveTimeoutRef.current = setTimeout(() => {
        void flushPendingSave()
      }, SAVE_DEBOUNCE_MS)
    },
    [canEdit, flushPendingSave, collaborative],
  )

  return { displayContent, handleContentChange }
}
